/* ============================================================
   Status registry — sorgente UNICA per la resa visiva degli stati
   pratica in tutti i verticali (RDI/DDT/BSC/Rinascente).

   NB: le CHIAVI di stato sono contratto backend (done/approved/
   discarded/rejected/…) e NON vanno rinominate — qui si mappa
   solo la resa (etichetta/variante/icona), unificata tra verticali.
   Etichette al femminile (pratica/fattura/richiesta) e colori
   coerenti ovunque. Gli stati di JOB (batch/adf) hanno tassonomia
   propria e restano fuori da questo registry.
   ============================================================ */

export type StatusVariant = "success" | "warning" | "danger" | "info" | "neutral";

export interface StatusMeta {
  label: string;
  variant: StatusVariant;
  icon: string; // material-symbols
}

export const STATUS_REGISTRY: Record<string, StatusMeta> = {
  // da lavorare
  needs_review:      { label: "Da validare",          variant: "warning", icon: "pending" },
  pending_review:    { label: "Da validare",          variant: "warning", icon: "pending" },
  pending_documents: { label: "In attesa documenti",  variant: "warning", icon: "hourglass_top" },
  pending:           { label: "In attesa",            variant: "neutral", icon: "hourglass_top" },
  // in corso
  received:          { label: "In elaborazione",      variant: "info",    icon: "sync" },
  reprocessing:      { label: "In rielaborazione",    variant: "info",    icon: "autorenew" },
  // esiti positivi
  done:              { label: "Approvata",            variant: "success", icon: "check_circle" },
  approved:          { label: "Approvata",            variant: "success", icon: "check_circle" },
  auto_approved:     { label: "Approvata (auto)",     variant: "success", icon: "check_circle" },
  // esiti negativi
  rejected:          { label: "Rifiutata",            variant: "danger",  icon: "cancel" },
  discarded:         { label: "Scartata",             variant: "danger",  icon: "delete_sweep" },
  not_invoice:       { label: "Non fattura",          variant: "neutral", icon: "block" },
  no_pdf:            { label: "Senza PDF",            variant: "neutral", icon: "help" },
  error:             { label: "Errore",               variant: "danger",  icon: "error" },
  // Consegna MOP: namespace di visualizzazione, le chiavi API restano invariate.
  mop_pending:       { label: "Da inviare",           variant: "warning", icon: "schedule_send" },
  mop_sent:          { label: "Inviato",              variant: "success", icon: "check_circle" },
  mop_error:         { label: "Errore di invio",      variant: "danger",  icon: "error" },
  mop_invalidated:   { label: "Da rigenerare",        variant: "neutral", icon: "update" },
};

const FALLBACK: StatusMeta = { label: "—", variant: "neutral", icon: "help" };

export function statusMeta(status: string): StatusMeta {
  return STATUS_REGISTRY[status] ?? { ...FALLBACK, label: status };
}

/** variante → token colore Indaco (per dot/icona) */
export const STATUS_VARIANT_COLOR: Record<StatusVariant, string> = {
  success: "var(--indaco-success)",
  warning: "var(--indaco-warning)",
  danger:  "var(--indaco-danger)",
  info:    "var(--indaco-info)",
  neutral: "var(--indaco-gray-500)",
};

/** Badge con etichetta + dot — usato nelle pagine di dettaglio */
export function StatusBadge({ status }: { status: string }) {
  const m = statusMeta(status);
  return (
    <span className={`badge badge-${m.variant}`}>
      <span className="status-dot" style={{ background: STATUS_VARIANT_COLOR[m.variant] }} />
      {m.label}
    </span>
  );
}

/** Icona-sola con tooltip — usato nelle liste */
export function StatusIcon({ status, size = 20 }: { status: string; size?: number }) {
  const m = statusMeta(status);
  return (
    <span title={m.label} style={{ display: "inline-flex", alignItems: "center" }}>
      <span className="material-symbols-outlined" style={{ fontSize: size, color: STATUS_VARIANT_COLOR[m.variant] }}>
        {m.icon}
      </span>
    </span>
  );
}
